import { apiRequestContractHandler } from "shared/api/apiRequestContractHandler";
import { postSocietyReviewInstanceAssignmentVoteContract } from "shared/api/contracts/society/societyId/review/instance/reviewInstanceId/assignment/assignmentId/vote";
import Spinner from "shared/components/common/Spinner";
import SurveyJSFormComponent, {
  surveyJSAddSpinnerToCompletedHtml,
  surveyJSRemoveSpinnerFromCompletedHtml,
} from "shared/components/survey-js-form/SurveyJSFormComponent";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type CompleteEvent, type SurveyModel } from "survey-core";
import { queryClient } from "hub/src/api/client/query-client";
import { ENV } from "hub/src/appConstants/env";
import { useHubReviewsVotingPageContext } from "hub/src/context/reviews/HubReviewsVotingPageContext";
import useDeviceInfo from "hub/src/hooks/useDeviceInfo";

export const HubReviewsVotingScorecardForm = () => {
  const session = useCustomSession();
  const router = useCustomRouter();
  const device = useDeviceInfo();
  const { reviewSession } = useHubReviewsVotingPageContext();
  const {
    reviewInstanceAssignment,
    reviewInstanceAssignmentIsFetching,
    nextInstanceId,
    formDesign,
    formDesignIsLoading,
  } = useHubReviewsVotingPageContext();
  const isLastReviewAssignment = !nextInstanceId;

  if (formDesignIsLoading || reviewInstanceAssignmentIsFetching) {
    return <Spinner />;
  }

  const allowModification =
    (reviewInstanceAssignment?.reviewInstance?.allowModification ?? 0) > 0;

  const hasVote =
    (reviewInstanceAssignment?.reviewInstance?.reviewInstanceVotes ?? [])
      .length > 0;

  const disableScorecard = hasVote && !allowModification;

  return (
    <div>
      <SurveyJSFormComponent
        baseUrl={ENV.REACT_APP_PILLAR_API_URL}
        className="surveyjs-wrap"
        isReadOnly={
          !session.adminMode && (!reviewSession.open || disableScorecard)
        }
        deviceType={device.platform}
        completeText="Submit Review"
        onComplete={async (
          sender: SurveyModel,
          options: CompleteEvent,
        ): Promise<void> => {
          const { clearSaveMessages, showSaveInProgress, showSaveError } =
            options;
          clearSaveMessages();
          showSaveInProgress("Saving your response");
          try {
            surveyJSAddSpinnerToCompletedHtml(sender);
            await apiRequestContractHandler(
              postSocietyReviewInstanceAssignmentVoteContract,
              {
                params: {
                  profileId: session.profileId!,
                  societyId: session.societyId!,
                  reviewInstanceId:
                    reviewInstanceAssignment.reviewInstance!.reviewInstanceId!,
                  assignmentId:
                    reviewInstanceAssignment.reviewInstanceAssignmentId!,
                },
                body: {
                  score: sender.data.score,
                  voteData: sender.data,
                  reviewInstanceVoteId: sender.data.reviewInstanceVoteId,
                },
              },
            );

            await queryClient.invalidateQueries({
              queryKey: ["reviewInstanceAssignment", "reviewSessions"],
              exact: false,
            });

            if (!isLastReviewAssignment) {
              router.push(
                `/reviews/${reviewSession.id}/instance/${nextInstanceId}`,
              );
            } else {
              router.push("/reviews");
            }

            if (session.profile?.profileId) {
              session.refreshSession();
            } else {
              //we need to wait for the session to refresh to  the profileId before we can redirect
              await session.refreshSession();
            }
            //Remove the spinner from the completedHtml since we are done processing that
            surveyJSRemoveSpinnerFromCompletedHtml(sender);
          } catch (error) {
            console.error(error);
            if (error instanceof Error) {
              showSaveError(error.message);
            }
          }
        }}
        formDesign={formDesign!.formDesign!}
        formData={{
          reviewInstanceId:
            reviewInstanceAssignment?.reviewInstance?.reviewInstanceId,
          assignmentId: reviewInstanceAssignment?.reviewInstanceAssignmentId,
          reviewInstanceVoteId:
            reviewInstanceAssignment?.reviewInstance?.reviewInstanceVotes![0]
              ?.id,
          ...(reviewInstanceAssignment?.reviewInstance?.reviewInstanceVotes![0]
            ?.voteData
            ? JSON.parse(
                JSON.stringify(
                  reviewInstanceAssignment?.reviewInstance
                    ?.reviewInstanceVotes![0].voteData,
                ),
              )
            : {}),
        }}
        spinner={<Spinner />}
      />
    </div>
  );
};
